// @ts-nocheck

import React, { useCallback, forwardRef, useState } from 'react';
import type { ElementRef } from 'react';
import styled, { css } from 'styled-components';

import { Cross } from '../../assets/svg/common';
import { Loupe } from '../../assets/svg/sports-menu';
import { Target } from '../../client';
import theme from '../../styled-components/dark';

const INPUT_HEIGHT = 50; // px
const INPUT_PADDING_RIGHT = INPUT_HEIGHT * 1.5;
const BORDER_RADIUS = 9999;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${INPUT_HEIGHT}px;
  border-radius: ${BORDER_RADIUS}px;

  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: center;

  background-color: ${Target.__MOBILE__ ? theme.SearchInput.backgroundColor : theme.SearchInput.light.backgroundColor};

  ${({ $focused }) => css`
    border: ${$focused
      ? `1px solid ${theme.SearchInput.highlightColor}`
      : `1px solid ${theme.SearchInput.backgroundColor}`};
  `}
`;

const LeftIcon = styled.div`
  width: ${INPUT_HEIGHT}px;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoupe = styled(Loupe)`
  width: 100%;
  height: 40%;
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 1;
  height: 100%;

  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: ${({ $hasCross }) => ($hasCross ? `0 ${INPUT_PADDING_RIGHT}px 0 0` : `0 1em 0 0`)};

  outline: none;
  border: none;
  border-top-right-radius: ${BORDER_RADIUS}px;
  border-bottom-right-radius: ${BORDER_RADIUS}px;

  background-color: transparent;

  ${Target.__MOBILE__
    ? css`
        font-size: 1.2em;
        color: ${theme.SearchInput.color};
      `
    : css`
        font-size: 1em;
        color: ${theme.SearchInput.light.color};
      `}

  &::placeholder {
    color: ${theme.SearchInput.placeholderColor};
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

const ResetBtn = styled.div`
  position: absolute;
  width: ${INPUT_HEIGHT * 0.3}px;
  height: ${INPUT_HEIGHT * 0.35}px;

  right: ${(INPUT_HEIGHT - INPUT_HEIGHT * 0.3) / 2}px;

  z-index: 10;

  cursor: pointer;
  transition: opacity 200ms ease-in-out;
  color: ${theme.SearchInput.highlightColor};

  > svg {
    width: 100%;
  }

  ${({ $show }) => {
    if ($show) {
      return css`
        opacity: 1;
      `;
    }
    return css`
      opacity: 0;
      pointer-events: none;
    `;
  }}
`;

type PropsType = {
  placeholder?: string;
  value: string;
  onChange: (val: string) => void;
  className?: string;
};

const SearchInput = forwardRef<PropsType, ElementRef<'input'>>(function SearchInput(
  { placeholder, value, onChange, className },
  inputRef,
) {
  const [isFocused, setIsFocused] = useState(false);

  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onResetBtnClick = useCallback(() => {
    if (value) {
      onChange('');
    }
  }, [onChange, value]);

  const onInputChange = useCallback(
    (e) => {
      const val = e?.target?.value ?? '';
      if (onChange) {
        onChange(val);
      }
    },
    [onChange],
  );

  return (
    <Wrapper className={className} $focused={isFocused}>
      <LeftIcon>
        <StyledLoupe />
      </LeftIcon>
      <InputWrapper>
        <Input
          ref={inputRef}
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          onChange={onInputChange}
          $hasCross={!!value}
        />
        <ResetBtn onClick={onResetBtnClick} $show={!!value}>
          <Cross />
        </ResetBtn>
      </InputWrapper>
    </Wrapper>
  );
});

export default SearchInput;
